import { useEffect, useState } from 'react';
import { useRequiredParams, useEditorSearch, useIsViewingVersion } from 'app/router/params';

import { Editor } from 'app/components/editor/Editor';
import Navbar from 'app/components/editor/navbar/Navbar';
import Sidebar from 'app/components/editor/sidebar/Sidebar';
import { CommentJump } from 'app/components/editor/sidebar/comments/CommentJump';
import { SubToolbar } from 'app/components/editor/subtoolbar/SubToolbar';
import { SubNavBar } from 'app/components/editor/navbar/SubNavbar';
import withMentions from 'slate/extensions/mentions';
import withImportArea from 'slate/extensions/import-area';
import withNotes from 'slate/extensions/notes';
import withPage from 'slate/extensions/page';
import withParagraph from 'slate/extensions/paragraph';
import withLayout from 'slate/extensions/layout';
import withImage from 'slate/extensions/image';
import withReferenceTable from 'slate/extensions/reference-table';
import withTableOfContent from 'slate/extensions/table-of-content';
import useLock from './useLock';
import ReadOnlyMark from 'app/components/editor/ReadOnlyMark';
import { usePermissions } from 'app/api/permission-queries';
import withBookmarks from 'slate/extensions/bookmark';
import withLinks from 'slate/extensions/link';
import { useBookmarks } from 'entities/bookmark';
import withStyledParagraph from 'slate/extensions/styled-paragraph';
import withHighlight from 'slate/extensions/highlight';
import withTag from 'slate/extensions/tag';
import { TagContextProvider } from 'app/state/contexts/TagContext';
import { SidebarContextProvider } from 'app/state/contexts/SidebarContext';
import { EditorModeContextProvider, useEditorMode } from 'app/state/contexts/EditorContext';
import { withLists } from 'slate/extensions/list';
import { withDecorateComments } from 'slate/decorators/commentDecorator';
import { TagsToolbar } from 'app/components/editor/subtoolbar/tags/TagsToolbar';
import { TagTextDeleteWarning } from 'app/components/modal/TagTextDeleteWarning';
import { TagSplitContinuation } from 'app/components/editor/TagSplitContinuation';

type MutationCallback = (a: { id?: string; documentSuperId?: string }) => void;

export const DocumentEditor = () => {
  return (
    <EditorModeContextProvider>
      <TagContextProvider>
        <SidebarContextProvider>
          <DocumentEditorInner />
        </SidebarContextProvider>
      </TagContextProvider>
    </EditorModeContextProvider>
  );
};

const DocumentEditorInner = () => {
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const { revision: documentRevisionId, sectionRevision: sectionRevisionId, translation: translationId, useDraft } = useEditorSearch();
  const isDraft = useDraft ?? false;
  const instance = { documentId, documentRevisionId, sectionId, sectionRevisionId, translationId };
  const [showFormatting, setShowFormatting] = useState(false);
  const isViewingVersion = useIsViewingVersion();
  const [isReadOnly, setIsReadOnly] = useState(isViewingVersion);
  const { sectionPermissions, documentPermissions, isSuccess: permissionsLoaded } = usePermissions(documentId);
  const { isTaggingMode } = useEditorMode();
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');
  const taggingLock = isTaggingMode && canEditTags && sectionPermissions[sectionId] === 'COMMENT';
  const { hasLock, lockInfo, deleteLock } = useLock(documentId, sectionId, !isViewingVersion, { taggingLock });
  const showSubToolbar = Object.values(sectionPermissions).some((value) => value);
  const hasSectionWritePermission = sectionPermissions[sectionId] === 'WRITE';
  const toolbarComponents = { styling: !isReadOnly, insert: !isReadOnly, languange: true };
  const { deleteLink, deleteBookmark } = useBookmarks(documentId);
  useEffect(() => {
    setIsReadOnly(isViewingVersion || !hasLock || !!isDraft || showFormatting || isTaggingMode);
  }, [isViewingVersion, hasLock, isDraft, showFormatting, isTaggingMode]);
  const extensions = [
    withLayout({ markEmpty: true }),
    withPage({ withButtons: true, readOnly: isReadOnly }),
    withMentions,
    withImportArea({ readOnly: isReadOnly, withComments: !isViewingVersion, withTags: true }),
    withImage({ readOnly: isReadOnly }),
    withPage({ withButtons: true, readOnly: isReadOnly }),
    withNotes,
    withParagraph({ withComments: !isViewingVersion, withStyledParagrah: hasSectionWritePermission && !isViewingVersion, withTags: true }),
    withReferenceTable({ readOnly: isReadOnly }),
    withTableOfContent({ readOnly: isReadOnly, hasWritePermission: hasSectionWritePermission }),
    withBookmarks({ deleteBookmark: deleteBookmark as MutationCallback }),
    withLinks({ deleteLink: deleteLink as MutationCallback }),
    withStyledParagraph({ isPDF: false }),
    withHighlight({}),
    withTag({ isPDF: false }),
    withLists({}),
  ];
  const decorators = [withDecorateComments({ sectionSuperId: sectionId })];
  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (hasLock) {
        await deleteLock();
      }
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('popstate', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handleBeforeUnload);
      if (hasLock) {
        void deleteLock();
      }
    };
  }, [hasLock, deleteLock]);

  return (
    <div className={`document-editor ${showSubToolbar ? 'sub-navbar-padding' : ''}`}>
      <Editor sectionHeader autoSave showFormatting={showFormatting} instance={instance} extensions={extensions} readOnly={isReadOnly} decorators={decorators}>
        <Navbar readOnly={isReadOnly} isDraft={isDraft} />
        {!isTaggingMode && <ReadOnlyMark readOnly={isReadOnly} isViewingVersion={isViewingVersion} lockOwner={lockInfo.lockOwner} />}
        <SubNavBar visibile={showSubToolbar}>
          <ToolbarSwitcher toolbarComponents={toolbarComponents} showFormatting={showFormatting} setShowFormatting={setShowFormatting} />
        </SubNavBar>
        <Sidebar isSideBySide={false} setRevisionL={() => {}} readOnly={isReadOnly} hasWritePermission={hasSectionWritePermission} />
        <CommentJump />
        <TagTextDeleteWarning />
        {!translationId && <TagSplitContinuation canEditTags={permissionsLoaded ? canEditTags : undefined} />}
      </Editor>
    </div>
  );
};

type ToolbarSwitcherProps = {
  toolbarComponents: { styling: boolean; insert: boolean; languange: boolean };
  showFormatting: boolean;
  setShowFormatting: (value: boolean) => void;
};

const ToolbarSwitcher = ({ toolbarComponents, showFormatting, setShowFormatting }: ToolbarSwitcherProps) => {
  const { mode } = useEditorMode();

  if (mode === 'tagging') {
    return <TagsToolbar />;
  }
  return <SubToolbar enabled={toolbarComponents} showFormatting={showFormatting} setShowFormatting={setShowFormatting} />;
};
